import * as Yup from "yup";

/**
 * Validation and totals for the Tosite form, kept out of the component so the
 * rules can be tested directly rather than through the rendered page.
 */

export type TransactionFormRow = {
  description: string;
  debit: string;
  credit: string;
  accountCode: number | "";
};

/** Empty means zero. Accepts the Finnish decimal comma. */
export const parseAmt = (v: unknown): number => {
  if (v === "" || v == null) return 0;
  const s = String(v).replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

/** Cents, so 0.1 + 0.2 never shows up as a phantom mismatch. */
const toCents = (v: unknown): number => Math.round(parseAmt(v) * 100);

export type VoucherTotals = {
  debit: number;
  credit: number;
  difference: number;
};

/** Running debet/kredit totals for the form's summary line. */
export const computeTotals = (
  transactions: Pick<TransactionFormRow, "debit" | "credit">[]
): VoucherTotals => {
  let debitCents = 0;
  let creditCents = 0;

  for (const tx of transactions ?? []) {
    const d = toCents(tx?.debit);
    const c = toCents(tx?.credit);
    if (Number.isFinite(d)) debitCents += d;
    if (Number.isFinite(c)) creditCents += c;
  }

  return {
    debit: debitCents / 100,
    credit: creditCents / 100,
    difference: Math.abs(debitCents - creditCents) / 100,
  };
};

const MISSING_AMOUNT = "Anna summa joko debet- tai kredit-puolelle";

const amountField = (label: "Debet" | "Kredit") =>
  Yup.string()
    .transform((v) => (v == null ? "" : String(v)))
    .test("is-number", `${label} pitää olla numero`, (v) =>
      v === "" ? true : Number.isFinite(parseAmt(v))
    )
    .test("non-negative", `${label} ei voi olla negatiivinen`, (v) =>
      v === "" ? true : parseAmt(v) >= 0
    );

/**
 * "At least one side must carry an amount", attached to a *field* rather than
 * the row object. An object-level test reports at path `transactions[0]`, which
 * Formik stores as a string; the form reads `errors.transactions[i].debit`, so
 * `.debit` on a string is undefined and the message never renders — submit just
 * silently does nothing. Keeping it on the field gives it a path the form
 * displays. Uses `function` (not an arrow) for `this.parent` sibling access.
 */
const withMissingAmountCheck = (
  field: Yup.StringSchema<string | undefined>,
  sibling: "debit" | "credit"
) =>
  field.test("one-sided-amount", MISSING_AMOUNT, function (v) {
    const own = parseAmt(v);
    const other = parseAmt(this.parent?.[sibling]);
    // Defer to is-number / non-negative so their specific message wins.
    if (!Number.isFinite(own) || !Number.isFinite(other)) return true;
    if (own < 0 || other < 0) return true;
    return own > 0 || other > 0;
  });

export const makeValidationSchema = (accountCodes: number[]) => {
  const txRowSchema = Yup.object({
    description: Yup.string()
      .max(120, "Max 120 merkkiä")
      .required("Anna kuvaus"),
    accountCode: Yup.mixed()
      .transform((v) => (v === "" || v == null ? null : Number(v)))
      .oneOf(accountCodes, "Valitse tili")
      .required("Valitse tili"),
    // Neither side is required: leaving the unused one blank is the normal way
    // to book a row, and it reads as zero. The missing-amount check below is
    // what stops an empty row getting through.
    debit: withMissingAmountCheck(amountField("Debet"), "credit"),
    credit: withMissingAmountCheck(amountField("Kredit"), "debit"),
  });

  // Note: debet = kredit is deliberately NOT validated here. Nothing enforced
  // it before, so blocking would stop vouchers that save today. The form shows
  // a mismatch warning via computeTotals instead.
  return Yup.object({
    description: Yup.string()
      .required("Nimi on pakollinen")
      .max(160, "Max 160 merkkiä"),
    date: Yup.date()
      .typeError("Valitse kelvollinen pvm")
      .required("Pvm on pakollinen"),
    transactions: Yup.array()
      .of(txRowSchema)
      .min(1, "Lisää vähintään yksi tapahtuma"),
  });
};

/** Form rows carry strings; the API column is numeric. */
export const toTransactionPayload = (transactions: TransactionFormRow[]) =>
  (transactions ?? []).map((tx) => ({
    ...tx,
    debit: parseAmt(tx.debit),
    credit: parseAmt(tx.credit),
  }));
